import { FC, lazy, Suspense } from 'react';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import { useParams, useSearchParams } from 'react-router';
import { usePolicy } from '@acorn-portal/shared/src/models/policy-model';
import { isTelematics } from '@acorn-portal/shared/src/utils/product-utils';

// Lazy load the policy detail pages for better code splitting
const TelematicsPolicyDetails = lazy(() => import('@acorn-portal/shared/src/pages/telematics/policy-details'));
const PolicyDetails = lazy(() => import('@acorn-portal/shared/src/pages/policy/policy-details'));

/**
 * Prefixes used to identify AcornProtect telematics policies.
 * - HVNTD: Standard AcornProtect policies
 * - HVNTY: Young Driver AcornProtect policies
 * - HVNYD: Legacy Young Driver AcornProtect prefix
 */
const ACORN_PROTECT_PREFIXES = ['HVNTD', 'HVNTY', 'HVNYD'] as const;

/**
 * Checks if a policy reference belongs to an AcornProtect telematics policy.
 *
 * @param policyNumber - The policy reference number to check
 * @returns true if the policy is an AcornProtect telematics policy (starts with HVNTD, HVNTY or HVNYD)
 *
 * @example
 * ```ts
 * isAcornProtectPolicy('HVNTD12345'); // Returns true
 * isAcornProtectPolicy('HVNTY02298540'); // Returns true
 * isAcornProtectPolicy('HVNYD12345'); // Returns true
 * isAcornProtectPolicy('POL12345'); // Returns false
 * ```
 */
export const isAcornProtectPolicy = (policyNumber: string | null | undefined): boolean => {
	if (!policyNumber) return false;
	const upperPolicyNumber = policyNumber.toUpperCase();
	return ACORN_PROTECT_PREFIXES.some((prefix) => upperPolicyNumber.startsWith(prefix));
};

/**
 * Router component that displays the appropriate policy details page based on policy type.
 *
 * For telematics policies (AcornProtect, Carrot, etc.), this component renders
 * the telematics-enabled policy details page which includes driver scores,
 * trip data, and telematics-specific features. For all other policies,
 * it renders the standard policy details page.
 *
 * Routing is determined by two complementary checks:
 * 1. **Policy number prefix** (fast path) — if the policy number starts with
 *    a known telematics prefix (HVNTD, HVNYD), the telematics layout renders
 *    immediately without waiting for the API.
 * 2. **Product type** (reliable path) — once the policy is loaded from the API,
 *    the product field is checked. This catches telematics policies whose
 *    numbers don't match the known prefixes.
 *
 * @component
 * @example
 * ```tsx
 * // In Routes.tsx
 * {
 *   path: `${ROUTE_PATHS.policyDetails}/:policyToken`,
 *   element: <AcornProtectPolicyRouter />,
 * }
 * ```
 */
const AcornProtectPolicyRouter: FC = () => {
	const [searchParams] = useSearchParams();
	const { policyToken } = useParams<{ policyToken: string }>();
	const policyNumber = searchParams.get('policyNumber');
	const { fullPolicy } = usePolicy(policyToken ?? null);

	// Fast path: check the policy number prefix
	const isPrefixMatch = isAcornProtectPolicy(policyNumber);

	// Reliable path: check the product type from loaded policy data
	const isPolicyLoaded = fullPolicy.policyToken === policyToken;
	const isProductTelematics = isPolicyLoaded && isTelematics(fullPolicy.product);

	const isTelematicsPolicy = isPrefixMatch || isProductTelematics;

	// If the prefix doesn't match and the policy hasn't loaded yet,
	// show a loading state to avoid briefly rendering the wrong layout
	if (!isPrefixMatch && !isPolicyLoaded) {
		return <FullPageLoadingSplash message='Loading Policy Details...' loading />;
	}

	return (
		<Suspense fallback={<FullPageLoadingSplash message='Loading Policy Details...' loading />}>
			{isTelematicsPolicy ? <TelematicsPolicyDetails /> : <PolicyDetails />}
		</Suspense>
	);
};

export default AcornProtectPolicyRouter;
